/**
 * Kid prompt entry — the "what should your book be about?" screen.
 *
 * Large tap targets, a few tappable idea chips for pre-literate kids, and a big
 * submit button. On submit it asks the backend to start generating and hands
 * the returned stream URL up to the parent (which routes to the reader).
 */

import { useState } from "react";

import { ApiError, books } from "@/lib/api";

interface PromptEntryProps {
  childId: string;
  /** Called with the new book id + SSE stream URL once generation starts. */
  onBookStarted: (bookId: string, streamUrl: string) => void;
}

type BookType = "fiction" | "nonfiction";

const IDEA_CHIPS: Record<BookType, string[]> = {
  fiction: [
    "a brave little fox",
    "a dragon who loves cake",
    "a trip to the moon",
    "a puppy's first day at school",
    "a friendly robot",
    "an underwater city",
  ],
  nonfiction: [
    "how rainbows are made",
    "why the moon changes shape",
    "how bees make honey",
    "what dinosaurs ate",
    "how volcanoes work",
    "why we need sleep",
  ],
};

const BOOK_TYPES: { value: BookType; label: string; emoji: string }[] = [
  { value: "fiction", label: "Make-believe story", emoji: "🦊" },
  { value: "nonfiction", label: "True facts", emoji: "🔎" },
];

export function PromptEntry({ childId, onBookStarted }: PromptEntryProps) {
  const [prompt, setPrompt] = useState("");
  const [bookType, setBookType] = useState<BookType>("fiction");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = prompt.trim().length >= 3 && !submitting;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { bookId, streamUrl } = await books.create({
        childId,
        prompt: prompt.trim(),
        bookType,
      });
      onBookStarted(bookId, streamUrl);
    } catch (err) {
      const offline = !navigator.onLine;
      setError(
        offline
          ? "You're offline right now. New books need the internet — but you can re-read your saved books!"
          : err instanceof ApiError && err.status === 429
            ? "The storyteller is busy. Please try again in a moment."
            : "Something went wrong. Let's try again!",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Make a new book">
      <h1 style={{ fontFamily: "var(--font-reader)", fontSize: "2rem" }}>
        What should your book be about?
      </h1>

      <fieldset
        style={{ border: "none", padding: 0, margin: "0 0 1.5rem" }}
      >
        <legend style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>
          Pick a kind of book
        </legend>
        <div
          role="radiogroup"
          aria-label="Kind of book"
          style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}
        >
          {BOOK_TYPES.map((type) => {
            const selected = bookType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`kid-button ${
                  selected ? "kid-button--primary" : "kid-button--secondary"
                }`}
                style={{ fontSize: "1.25rem" }}
                onClick={() => setBookType(type.value)}
              >
                {type.emoji} {type.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <label htmlFor="prompt" style={{ display: "block", fontSize: "1.25rem" }}>
        {bookType === "nonfiction" ? "Tell me what to learn about" : "Tell me your idea"}
      </label>
      <textarea
        id="prompt"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        maxLength={200}
        placeholder={
          bookType === "nonfiction"
            ? "how rainbows are made"
            : "a brave little fox who finds a magic key"
        }
        style={{
          width: "100%",
          fontSize: "1.5rem",
          fontFamily: "var(--font-ui)",
          padding: "1rem",
          borderRadius: "var(--radius-kid)",
          border: "3px solid var(--color-grape)",
          marginBottom: "1rem",
          boxSizing: "border-box",
        }}
      />

      <div
        role="group"
        aria-label={bookType === "nonfiction" ? "Topic ideas" : "Story ideas"}
        style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem" }}
      >
        {IDEA_CHIPS[bookType].map((idea) => (
          <button
            key={idea}
            type="button"
            className="kid-button kid-button--secondary"
            style={{ fontSize: "1.1rem" }}
            onClick={() => setPrompt(idea)}
          >
            {idea}
          </button>
        ))}
      </div>

      {error && (
        <p role="alert" style={{ color: "var(--color-grape)", fontSize: "1.25rem" }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        className="kid-button kid-button--primary"
        style={{ fontSize: "1.5rem", width: "100%" }}
        disabled={!canSubmit}
        aria-busy={submitting}
      >
        {submitting ? "✨ Making your book…" : "Make my book!"}
      </button>
    </form>
  );
}
